<script lang="ts">
import { useRouter } from 'vue-router'
import { defineComponent } from 'vue'
import MailDataService from '@/services/MailDataService'
import type ResponseData from '@/types/ResponseData'
import type { LaravelPaginatedResponse } from '@/types/ApiResponses'
import type Mail from '@/types/Mail'
import type ObjectConfig from '@/types/ObjectConfig'
import * as _ from 'lodash'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

export default defineComponent({
  name: 'mail-list', data() {
    return {
      mails: [] as Mail[],
      router: useRouter(),
      filterMails: '',
      loaded: false,
      selectedColumn: 'id',
      selectedDirection: 'desc',
      pagination: {} as LaravelPaginatedResponse<Mail>['data'],
      mailStatuses: [] as ObjectConfig[],
      selectedMailIds: [] as number[],
      bulkSelectedStatus: '',
      isBulkUpdating: false,
      _searchTimer: undefined as ReturnType<typeof setTimeout> | undefined,
    }
  },
  methods: {
    retrieveMails(page: number, type: string) {
      this.selectedMailIds = [];
      if (type == 'prev') {
        page--;
      } else if (type == 'next') {
        page++;
      } MailDataService.getAllMails(page, this.filterMails)
        .then((response: ResponseData) => {
          this.mails = response.data.data.data;
          this.pagination = response.data.data;
          this.loaded = true
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
          this.loaded = true
        })
    },
    addMail() {
      this.router.push({
        name: 'Pridať zásielku',
      })
    },
    getMailStatuses() {
      MailDataService.getMailStatuses()
        .then((response: ResponseData) => {
          this.mailStatuses = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getStatusName(status: number) {
      const found = this.mailStatuses.find((s) => s.id == status)
      return found ? found.description || found.name : status
    },
    allVisibleMailsSelected() {
      if (!this.mails?.length) return false
      return this.mails.every((mail: Mail) => this.selectedMailIds.includes(mail.id))
    },
    toggleSelectAllVisible(event: Event) {
      const target = event.target as HTMLInputElement
      if (target.checked) {
        this.selectedMailIds = this.mails.map((mail: Mail) => mail.id)
      } else {
        this.selectedMailIds = []
      }
    },
    toggleMailSelection(mailId: number, event: Event) {
      const target = event.target as HTMLInputElement
      if (target.checked) {
        if (!this.selectedMailIds.includes(mailId)) {
          this.selectedMailIds.push(mailId)
        }
      } else {
        this.selectedMailIds = this.selectedMailIds.filter((id) => id !== mailId)
      }
    },
    async applyBulkStatusUpdate() {
      if (!this.selectedMailIds.length) {
        toast.error('Chyba: Vyberte aspoň jednu zásielku.')
        return
      }
      if (!this.bulkSelectedStatus) {
        toast.error('Chyba: Vyberte stav pre hromadnú zmenu.')
        return
      }
      this.isBulkUpdating = true
      try {
        const statusId = Number(this.bulkSelectedStatus)
        const response = await MailDataService.bulkUpdateStatus(this.selectedMailIds, statusId)
        const updatedCount = response?.data?.data?.updated_count ?? this.selectedMailIds.length
        const alreadyInStatusCount = response?.data?.data?.already_in_status_count ?? 0
        toast.success(`Stav bol úspešne zmenený pre ${updatedCount} zásielok.`)
        if (alreadyInStatusCount > 0) {
          toast.info(`${alreadyInStatusCount} zásielok už malo tento stav.`)
        }
        this.selectedMailIds = []
        this.retrieveMails(this.pagination.current_page || 1, '')
      } catch (e: any) {
        toast.error('Chyba: Hromadná zmena stavu zlyhala.')
      } finally {
        this.isBulkUpdating = false
      }
    },
    setOrderResultsBy(column: string) {
      if (this.selectedColumn == column) {
        if (this.selectedDirection == 'desc') {
          this.selectedDirection = 'asc'
        } else {
          this.selectedDirection = 'desc'
        }
      } else {
        this.selectedDirection = 'asc'
      }
      this.selectedColumn = column
    },
  },
  computed: {
    orderedItems() {
      switch (this.selectedColumn) {
        case 'id':
          return _.orderBy(this.mails, ['id'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'sender':
          return _.orderBy(this.mails, ['sender'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'status':
          return _.orderBy(this.mails, [(mail: Mail) => this.getStatusName(mail.status)], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'date':
          return _.orderBy(
            this.mails,
            ['updated_at'],
            [this.selectedDirection.includes('asc') ? 'asc' : 'desc'],
          )
        case 'name':
          return _.orderBy(this.mails, ['name'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        default:
          return _.orderBy(this.mails, ['id'], ['desc'])
      }
    },
  },
  watch: {
    filterMails: {
      handler() {
        clearTimeout(this._searchTimer)
        this._searchTimer = setTimeout(() => {
          this.retrieveMails(1, '')
        }, 300)
      },
    },
  },
  mounted() {
    this.retrieveMails(1, '')
    this.getMailStatuses()
  },
})
</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <h1 class="text-2xl font-semibold text-gray-900">Zoznam zásielok</h1>
      </div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div class="py-4">
          <div class="flex flex-col">
            <div class="overflow-x-auto">
              <div class="flex flex-row justify-between">
                <div class="relative max-w-xs">
                  <label for="search" class="sr-only"> Hľadať </label>
                  <FormKit v-model="filterMails" type="search" name="search"
                    class="block w-full p-3 pl-10 text-sm border-gray-200 rounded-md focus:border-teal-500 focus:ring-teal-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                    placeholder="Hľadať..." />
                </div>
                <div class="flex flex-row gap-2 basis-1/2 justify-end">
                  <select v-model="bulkSelectedStatus" name="bulk-status" id="bulk-status"
                    class="block p-2 text-sm border border-gray-200 rounded-md shadow-sm focus:border-teal-500 focus:ring-teal-500">
                    <option value="">Hromadná zmena stavu</option>
                    <option v-for="status in mailStatuses" :key="`bulk-${status.id}`" :value="String(status.id)">
                      {{ status.description || status.name }}
                    </option>
                  </select>
                  <button type="button" @click="applyBulkStatusUpdate()"
                    :disabled="!selectedMailIds.length || !bulkSelectedStatus || isBulkUpdating"
                    class="flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50">
                    {{ isBulkUpdating ? 'Ukladám...' : `Zmeniť stav (${selectedMailIds.length})` }}
                  </button>
                  <button type="button" @click="addMail()"
                    class="flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                    Pridať zásielku
                  </button>
                </div>
              </div>
              <div class="p-1.5 w-full inline-block align-middle" v-if="!loaded">
                <div class="text-center">
                  <div role="status">
                    <svg class="inline mr-2 w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-teal-600"
                      viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor" />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill" />
                    </svg>
                    <span class="sr-only">Loading...</span>
                  </div>
                </div>
              </div>
              <div class="columns" v-if="loaded && !mails?.length">
                <p>No items found</p>
              </div>
              <div class="p-1.5 w-full inline-block align-middle" v-if="loaded && mails?.length">
                <div class="overflow-hidden border rounded-lg">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th scope="col" class="px-4 py-3 text-left">
                          <input type="checkbox" :checked="allVisibleMailsSelected()" @change="toggleSelectAllVisible"
                            :disabled="!mails?.length"
                            class="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                        </th>
                        <th scope="col"
                          class="flex items-center px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center" :class="{ 'text-teal-500': selectedColumn == 'id' }">
                            ID
                            <button>
                              <svg @click="setOrderResultsBy('id')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center" :class="{ 'text-teal-500': selectedColumn == 'name' }">
                            Adresát
                            <button>
                              <svg @click="setOrderResultsBy('name')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center"
                            :class="{ 'text-teal-500': selectedColumn == 'sender' }">
                            Odosielateľ
                            <button>
                              <svg @click="setOrderResultsBy('sender')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center"
                            :class="{ 'text-teal-500': selectedColumn == 'status' }">
                            Stav
                            <button>
                              <svg @click="setOrderResultsBy('status')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center"> Súbor na scan </span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center" :class="{ 'text-teal-500': selectedColumn == 'date' }">
                            Dátum zmeny
                            <button>
                              <svg @click="setOrderResultsBy('date')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-right text-gray-500 uppercase">
                          Akcie
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-right text-gray-500 uppercase">Edit</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                      <tr class="hover:bg-gray-200" :class="[mail.status != 1 && 'bg-gray-50']"
                        v-for="mail in orderedItems" :key="mail.id">
                        <td class="px-4 py-4 text-center">
                          <input type="checkbox" :checked="selectedMailIds.includes(mail.id)"
                            @change="toggleMailSelection(mail.id, $event)"
                            class="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                        </td>
                        <td class="px-6 py-4 text-sm font-medium text-gray-800">{{ mail.id }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ mail.name }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ mail.sender }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ getStatusName(mail.status) }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">
                          <input id="scan" disabled :checked="mail.scan != null ? true : false" type="checkbox"
                            class="w-4 h-4 focus:ring-teal-500 focus:border-teal-500 block shadow-sm sm:text-sm border-gray-300 rounded-md text-teal-500" />
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ mail.updated_at }}</td>
                        <td class="flex flex-row justify-end px-6 py-4 text-sm font-medium text-right">
                          <div v-if="mail.scan_requested">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                              stroke="currentColor" class="w-6 h-6">
                              <path stroke-linecap="round" stroke-linejoin="round"
                                d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                            </svg>
                          </div>
                          <div v-if="mail.forward_requested">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                              stroke="currentColor" class="w-6 h-6">
                              <path stroke-linecap="round" stroke-linejoin="round"
                                d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                            </svg>
                          </div>
                          <div v-if="mail.shred_requested">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                              stroke="currentColor" class="w-6 h-6">
                              <path stroke-linecap="round" stroke-linejoin="round"
                                d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125z" />
                            </svg>
                          </div>
                        </td>
                        <td class="px-6 py-4 text-sm font-medium text-right">
                          <router-link :to="'/mail-detail/' + mail.id"
                            class="text-green-500 hover:text-green-700">Edit</router-link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                    <div class="flex flex-1 justify-between sm:hidden">
                      <a href="#"
                        class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Previous</a>
                      <a href="#"
                        class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Next</a>
                    </div>
                    <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p class="text-sm text-gray-700">
                          Stránka
                          <span class="font-medium">{{ pagination.current_page }}</span>
                          z
                          <span class="font-medium">{{ pagination.last_page }}</span>
                        </p>
                      </div>
                      <div>
                        <nav class="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            class="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                            :disabled="pagination.current_page <= 1"
                            @click="retrieveMails(pagination.current_page, 'prev')">
                            <span class="sr-only">Previous</span>
                            <!-- Heroicon name: mini/chevron-left -->
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                              fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd"
                                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                                clip-rule="evenodd" />
                            </svg>
                          </button>
                          <button v-for="item in pagination.last_page" :key="item" @click="retrieveMails(item, '')"
                            class="relative inline-flex items-center border text-gray-500 hover:bg-gray-50 bg-white px-4 py-2 text-sm font-medium border-gray-300"
                            :class="item == pagination.current_page
                              ? 'z-10 bg-teal-50 border-teal-500 text-teal-600'
                              : 'border-gray-300 hover:bg-gray-50 focus:z-20'
                              ">
                            {{ item }}
                          </button>
                          <button
                            class="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                            :disabled="pagination.current_page >= pagination.last_page"
                            @click="retrieveMails(pagination.current_page, 'next')">
                            <span class="sr-only">Next</span>
                            <!-- Heroicon name: mini/chevron-right -->
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                              fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd"
                                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                clip-rule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
